import { Dispatch, SetStateAction, useState } from 'react'
import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from 'react-query'
import { DefaultMainPagination, UnlimitedPagination } from '../constants/pagination'
import { Pagination } from '../interfaces/pagination'
import { ObjectKeyType, generateItemKey, generateListKey } from '../utils/key-generator'
import { ErrorResponse, useRequestHandler } from './useRequestHandler'
import { useSearchValue } from './useSearchValue'

export type NonQueryAttributes = 'sort' | 'order' | 'limit' | 'skip' | 'program_id' | 'search'

export interface IGetManyResponse<T> {
  data: T[]
  count: number
  total: number
  page: number
  page_count: number
  access_limited?: boolean
}

export interface IFetchProps<QueryObject extends Object> {
  sort?: string
  order?: 'ASC' | 'DESC'
  query: QueryObject
}

export interface IFetchManyProps<DataObject, QueryObject extends Object> {
  type: ObjectKeyType
  queryFn: (
    props: IFetchProps<QueryObject> & {
      programId: string
      skip?: number
      limit?: number
      search?: string
      signal?: AbortSignal
    },
  ) => Promise<IGetManyResponse<DataObject>>
  programId?: string
  fetchProps?: IFetchProps<QueryObject>
  defaultFetchProps: IFetchProps<QueryObject>
  pagination?: Pagination
  defaultPagination?: Pagination
  search?: string
  isEnabled?: boolean
  setFetchProps?: Dispatch<SetStateAction<IFetchProps<QueryObject>>>
  setPagination?: Dispatch<Pagination>
  setSearch?: Dispatch<string>
}

interface IFetchMany<DataObject, QueryObject extends Object> {
  data: DataObject[]
  error?: ErrorResponse
  fetchProps: IFetchProps<QueryObject>
  isError: boolean
  isLoading: boolean
  pagination: Pagination
  search: string
  setPagination: Dispatch<Pagination>
  setSearch: Dispatch<string>
  setFetchProps: (value?: IFetchProps<QueryObject>) => void
}

export const useFetchMany = <DataObject, QueryObject extends Object>({
  type,
  programId,
  fetchProps: controlledFetchProps,
  defaultFetchProps,
  pagination: controlledPagination,
  defaultPagination = DefaultMainPagination,
  search: controlledSearch,
  isEnabled = true,
  queryFn: fetchMany,
  setFetchProps: controlledSetFetchProps,
  setPagination: controlledSetPagination,
  setSearch: controlledSetSearch,
}: IFetchManyProps<DataObject, QueryObject>): IFetchMany<DataObject, QueryObject> => {
  const { requestHandler } = useRequestHandler()
  const [uncontrolledFetchProps, setUncontrolledFetchProps] =
    useState<IFetchProps<QueryObject>>(defaultFetchProps)
  const fetchProps = controlledFetchProps ?? uncontrolledFetchProps
  const setFetchProps = controlledSetFetchProps ?? setUncontrolledFetchProps
  const [uncontrolledPagination, setUncontrolledPagination] =
    useState<Pagination>(defaultPagination)
  const pagination = controlledPagination ?? uncontrolledPagination
  const setPagination = controlledSetPagination ?? setUncontrolledPagination
  const {
    search: uncontrolledSearch,
    deferSearch: uncontrolledDeferSearch,
    setSearch: setUncontrolledSearch,
    isSearching,
  } = useSearchValue(controlledSearch, {
    onDefer: () => setPagination(controlledPagination || defaultPagination),
  })
  const search = controlledSearch ?? uncontrolledSearch
  const deferSearch = controlledSearch ?? uncontrolledDeferSearch
  const setSearch = controlledSetSearch ?? setUncontrolledSearch
  const queryKeys = generateListKey({
    type,
    program_id: programId,
    pagination,
    sort: fetchProps.sort,
    order: fetchProps.order,
    query: fetchProps.query,
    search: deferSearch,
  })
  const {
    isLoading,
    isError,
    data: resp,
    error,
  } = useQuery<IGetManyResponse<DataObject>, ErrorResponse>(
    queryKeys,
    async ({ signal }) => {
      if (!programId) {
        throw new Error('Unable to fetch data')
      }
      const skip = pagination.limit ? pagination.page * pagination.limit : undefined
      return await requestHandler<IGetManyResponse<DataObject>>(() =>
        fetchMany({
          programId,
          skip,
          limit: pagination.limit,
          order: fetchProps.order || undefined,
          sort: fetchProps.sort || undefined,
          query: fetchProps.query,
          search: deferSearch || undefined,
          signal,
        }),
      )
    },
    {
      enabled: !!programId && isEnabled,
    },
  )

  const _setFetchProps = (val?: IFetchProps<QueryObject>) => {
    setPagination(defaultPagination)
    return setFetchProps(val || defaultFetchProps)
  }

  return {
    data: resp?.data || [],
    error: error || undefined,
    fetchProps,
    isError,
    isLoading: isLoading || isSearching,
    pagination: {
      ...pagination,
      count: resp?.count || pagination.count,
      total: resp?.total || pagination.total,
      page: resp?.page || pagination.page,
      page_count: resp?.page_count || pagination.page_count,
    },
    search,
    setFetchProps: _setFetchProps,
    setPagination,
    setSearch,
  }
}

export interface IInfiniteProps<DataObject, QueryObject extends Object>
  extends Omit<
      IFetchManyProps<DataObject, QueryObject>,
      'fetchProps' | 'defaultFetchProps' | 'isFilterPersisted' | 'setFetchProps' | 'setPagination'
    >,
    Required<Pick<IFetchManyProps<DataObject, QueryObject>, 'fetchProps'>> {
  fetchAll?: boolean
}

interface IFetchInfiniteMany<DataObject, QueryObject extends Object>
  extends Omit<IFetchMany<DataObject, QueryObject>, 'setFetchProps' | 'setPagination'> {
  isFetching: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<InfiniteQueryObserverResult<IGetManyResponse<DataObject>, ErrorResponse>>
}

export const useFetchInfiniteMany = <DataObject, QueryObject extends Object>({
  type,
  programId,
  fetchAll,
  fetchProps,
  pagination = DefaultMainPagination,
  search: controlledSearch,
  isEnabled = true,
  queryFn: fetchMany,
  setSearch: controlledSetSearch,
}: IInfiniteProps<DataObject, QueryObject>): IFetchInfiniteMany<DataObject, QueryObject> => {
  const { requestHandler } = useRequestHandler()
  const queryClient = useQueryClient()
  const {
    search: uncontrolledSearch,
    deferSearch: uncontrolledDeferSearch,
    setSearch: setUncontrolledSearch,
    isSearching,
  } = useSearchValue(controlledSearch)
  const search = controlledSearch ?? uncontrolledSearch
  const deferSearch = controlledSearch ?? uncontrolledDeferSearch
  const setSearch = controlledSetSearch ?? setUncontrolledSearch
  const queryKeys = generateListKey({
    type,
    program_id: programId,
    pagination: UnlimitedPagination,
    sort: fetchProps.sort,
    order: fetchProps.order,
    query: fetchProps.query,
    search: deferSearch,
  })

  const {
    data: resp,
    error,
    isError,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage = false,
  } = useInfiniteQuery<IGetManyResponse<DataObject>, ErrorResponse>(
    queryKeys,
    async ({ signal, pageParam: pg }) => {
      pg = pg || pagination
      if (!programId) {
        throw new Error('Unable to fetch data')
      }
      const skip = pg.limit ? pg.page * pg.limit : undefined
      const response = await requestHandler<IGetManyResponse<DataObject>>(() =>
        fetchMany({
          programId,
          skip,
          limit: pg.limit,
          order: fetchProps.order || undefined,
          sort: fetchProps.sort || undefined,
          query: fetchProps.query,
          search: deferSearch || undefined,
          signal,
        }),
      )
      response.data.forEach((item) => {
        if (typeof item === 'object' && !!item && 'id' in item && typeof item.id === 'string') {
          queryClient.setQueryData(
            generateItemKey({
              type,
              program_id: programId,
              id: item.id,
            }),
            item,
          )
        }
      })
      return response
    },
    {
      getNextPageParam: (resp: IGetManyResponse<DataObject>): Pagination | undefined => {
        if (!resp.page_count || resp.page < resp.page_count - 1) {
          return {
            ...pagination,
            count: resp?.count,
            total: resp?.total,
            page: resp?.page + 1,
            page_count: resp?.page_count,
          }
        }
        return undefined
      },
      getPreviousPageParam: (resp: IGetManyResponse<DataObject>): Pagination | undefined => {
        if (!resp.page_count || resp.page <= resp.page_count) {
          return {
            ...pagination,
            count: resp?.count,
            total: resp?.total,
            page: resp?.page - 1,
            page_count: resp?.page_count,
          }
        }
        return undefined
      },
      enabled: !!programId && !!isEnabled,
    },
  )

  if (!(isFetching || isFetchingNextPage || isLoading) && hasNextPage && fetchAll) {
    fetchNextPage()
  }

  const response = [...(resp?.pages || [])].pop()
  const data = (resp?.pages || []).reduce<DataObject[]>((list, p) => [...list, ...p.data], []) || []

  return {
    data,
    error: error || undefined,
    fetchProps,
    hasNextPage,
    search,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading: isLoading || isSearching,
    pagination: {
      ...pagination,
      count: response?.count || pagination.count,
      total: response?.total || pagination.total,
      page: response?.page || pagination.page,
      page_count: response?.page_count || pagination.page_count,
    },
    fetchNextPage,
    setSearch,
  }
}
